<template>
    <div class="lic-analytics">
        <div class="list-controls">
            <div class="gui-form">
                <div class="row-box">
                    <div class="sel-inp-set">
                        <label>팀:</label>
                        <select v-model="state.selectedCrewId" @change="onChangeCrew">
                            <option value="all">모든 팀</option>
                            <option v-for="c in state.crews" :key="c[CREW_KEYS.id]" :value="c[CREW_KEYS.id]">
                                {{ c[CREW_KEYS.name] }}
                            </option>
                        </select>
                    </div>
                    <div class="sel-inp-set">
                        <label>기간:</label>
                        <div class="date-range-picker-wrapper">
                            <button 
                                type="button" 
                                class="date-range-btn"
                                @click="toggleDatePicker"
                            >
                                {{ getDateRangeText() }}
                            </button>
                            <button type="button" class="period-btn" @click="setPeriod(1)">최근 1개월</button>
                            <button type="button" class="period-btn" @click="setPeriod(3)">최근 3개월</button>
                            <button type="button" class="period-btn" @click="setPeriod(6)">최근 6개월</button>
                            
                            <div v-if="state.showDatePicker" class="date-picker-popup">
                                <div class="date-picker-header">
                                    <span class="date-picker-title">날짜 범위 선택</span>
                                    <button type="button" class="close-btn" @click="closeDatePicker">×</button>
                                </div>
                                <div class="date-picker-calendar">
                                    <div class="calendar-month">
                                        <div class="year-selector-row">
                                            <button type="button" @click="prevYear" class="year-nav-btn">«</button>
                                            <select v-model="state.selectedYear" @change="changeYear" class="year-select">
                                                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}년</option>
                                            </select>
                                            <button type="button" @click="nextYear" class="year-nav-btn">»</button>
                                        </div>
                                        <div class="month-selector-row">
                                            <button type="button" @click="prevMonth" class="month-nav-btn">‹</button>
                                            <select v-model="state.selectedMonth" @change="changeMonth" class="month-select">
                                                <option v-for="month in 12" :key="month" :value="month - 1">{{ month }}월</option>
                                            </select>
                                            <button type="button" @click="nextMonth" class="month-nav-btn">›</button>
                                        </div>
                                    </div>
                                    <div class="calendar-weekdays">
                                        <div class="weekday" v-for="day in ['일', '월', '화', '수', '목', '금', '토']" :key="day">{{ day }}</div>
                                    </div>
                                    <div class="calendar-days">
                                        <div 
                                            v-for="day in state.calendarDays" 
                                            :key="day.key"
                                            :class="['calendar-day', {
                                                'other-month': day.otherMonth,
                                                'selected-start': day.isStart,
                                                'selected-end': day.isEnd,
                                                'in-range': day.inRange,
                                                'today': day.isToday
                                            }]"
                                            @click="selectDate(day)"
                                        >
                                            {{ day.date }}
                                        </div>
                                    </div>
                                </div>
                                <div class="date-picker-footer">
                                    <div class="selected-range">
                                        <span v-if="state.tempStartDate && state.tempEndDate">
                                            {{ formatDisplayDate(state.tempStartDate) }} ~ {{ formatDisplayDate(state.tempEndDate) }}
                                        </span>
                                        <span v-else-if="state.tempStartDate">
                                            {{ formatDisplayDate(state.tempStartDate) }} ~ (종료일 선택)
                                        </span>
                                        <span v-else>시작일을 선택하세요</span>
                                    </div>
                                    <div class="date-picker-actions">
                                        <button type="button" class="cancel-btn" @click="cancelDateSelection">취소</button>
                                        <button 
                                            type="button" 
                                            class="apply-btn" 
                                            @click="applyDateSelection"
                                            :disabled="!state.tempStartDate || !state.tempEndDate"
                                        >
                                            적용
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="analytics-content" v-if="state.loading.analytics">
            <p>로딩 중...</p>
        </div>

        <div class="analytics-content" v-else-if="state.analytics">
            <div class="stats-grid">
                <div class="stat-card">
                    <div class="stat-label">할당된 BOX 수 / 보유 BOX 수</div>
                    <div class="stat-value">
                        {{ formatNumber(state.analytics.assignedQuantity) }} / {{ formatNumber(state.analytics.totalQuantity) }}
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-label">
                        활성 계정
                        <span class="tooltip-icon" title="선택된 날짜 범위에 제품을 사용한 계정 수">?</span>
                        / BOX 할당 계정
                    </div>
                    <div class="stat-value">
                        {{ formatNumber(state.analytics.activeUsers) }} / {{ formatNumber(state.analytics.assignedAccountCount) }}
                    </div>
                    <div class="stat-sub-label">총 계정 수</div>
                    <div class="stat-sub-value">{{ formatNumber(state.analytics.totalMembers) }}</div>
                </div>
                <div class="stat-card">
                    <div class="stat-label">
                        월 평균 사용일
                        <span class="tooltip-icon" title="제품 사용 일수 / 개월 수">?</span>
                    </div>
                    <div class="stat-value">{{ formatNumber(state.analytics.monthlyAverageUsage, 1) }}일</div>
                </div>
            </div>
        </div>

        <div class="analytics-content" v-else>
            <p class="muted">데이터가 없습니다.</p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'licenseAnalytics',
    data() {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1); // 기본값: 1개월 전
        
        // 날짜 포맷팅 (YYYY-MM-DD)
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            CREW_KEYS: { id: 'id', name: 'crewName' },
            state: {
                crews: [],
                selectedCrewId: 'all',
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                analytics: null,
                loading: {
                    crews: false,
                    analytics: false,
                },
                showDatePicker: false,
                currentMonth: new Date(),
                tempStartDate: null,
                tempEndDate: null,
                calendarDays: [],
                selectedYear: new Date().getFullYear(),
                selectedMonth: new Date().getMonth(),
            },
        };
    },
    methods: {
        formatNumber(value, decimals = 0) {
            if (value == null) return '0';
            return Number(value).toLocaleString('ko-KR', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            });
        },
        onChangeCrew() {
            this.fetchAnalytics();
        },
        toggleDatePicker() {
            this.state.showDatePicker = !this.state.showDatePicker;
            if (this.state.showDatePicker) {
                // 달력 열 때 현재 선택된 날짜를 임시 변수에 설정
                this.state.tempStartDate = this.state.startDate ? new Date(this.state.startDate) : null;
                this.state.tempEndDate = this.state.endDate ? new Date(this.state.endDate) : null;
                
                // 현재 선택된 날짜가 있으면 해당 월로 이동, 없으면 오늘 날짜의 월
                if (this.state.tempStartDate) {
                    this.state.currentMonth = new Date(this.state.tempStartDate.getFullYear(), this.state.tempStartDate.getMonth(), 1);
                } else {
                    this.state.currentMonth = new Date();
                }
                this.state.selectedYear = this.state.currentMonth.getFullYear();
                this.state.selectedMonth = this.state.currentMonth.getMonth();
                this.updateCalendar();
            }
        },
        closeDatePicker() {
            this.state.showDatePicker = false;
            // 취소 시 임시 변수 초기화
            this.state.tempStartDate = null;
            this.state.tempEndDate = null;
        },
        cancelDateSelection() {
            this.closeDatePicker();
        },
        applyDateSelection() {
            if (this.state.tempStartDate && this.state.tempEndDate) {
                this.state.startDate = this.formatDateForInput(this.state.tempStartDate);
                this.state.endDate = this.formatDateForInput(this.state.tempEndDate);
                this.closeDatePicker();
                this.fetchAnalytics();
            }
        },
        selectDate(day) {
            if (day.otherMonth) return;
            
            const selectedDate = new Date(this.state.currentMonth.getFullYear(), this.state.currentMonth.getMonth(), day.date);
            selectedDate.setHours(0, 0, 0, 0);
            
            if (!this.state.tempStartDate || (this.state.tempStartDate && this.state.tempEndDate)) {
                // 시작일 선택 또는 리셋
                this.state.tempStartDate = new Date(selectedDate);
                this.state.tempEndDate = null;
            } else if (this.state.tempStartDate && !this.state.tempEndDate) {
                // 종료일 선택
                const startDate = new Date(this.state.tempStartDate);
                startDate.setHours(0, 0, 0, 0);
                
                if (selectedDate.getTime() < startDate.getTime()) {
                    // 종료일이 시작일보다 이전이면 교체
                    this.state.tempEndDate = new Date(this.state.tempStartDate);
                    this.state.tempStartDate = new Date(selectedDate);
                } else {
                    this.state.tempEndDate = new Date(selectedDate);
                }
            }
            this.updateCalendar();
        },
        prevMonth() {
            this.state.currentMonth = new Date(this.state.currentMonth.getFullYear(), this.state.currentMonth.getMonth() - 1, 1);
            this.state.selectedYear = this.state.currentMonth.getFullYear();
            this.state.selectedMonth = this.state.currentMonth.getMonth();
            this.updateCalendar();
        },
        nextMonth() {
            this.state.currentMonth = new Date(this.state.currentMonth.getFullYear(), this.state.currentMonth.getMonth() + 1, 1);
            this.state.selectedYear = this.state.currentMonth.getFullYear();
            this.state.selectedMonth = this.state.currentMonth.getMonth();
            this.updateCalendar();
        },
        prevYear() {
            this.state.currentMonth = new Date(this.state.currentMonth.getFullYear() - 1, this.state.currentMonth.getMonth(), 1);
            this.state.selectedYear = this.state.currentMonth.getFullYear();
            this.state.selectedMonth = this.state.currentMonth.getMonth();
            this.updateCalendar();
        },
        nextYear() {
            this.state.currentMonth = new Date(this.state.currentMonth.getFullYear() + 1, this.state.currentMonth.getMonth(), 1);
            this.state.selectedYear = this.state.currentMonth.getFullYear();
            this.state.selectedMonth = this.state.currentMonth.getMonth();
            this.updateCalendar();
        },
        changeYear() {
            this.state.currentMonth = new Date(this.state.selectedYear, this.state.currentMonth.getMonth(), 1);
            this.updateCalendar();
        },
        changeMonth() {
            this.state.currentMonth = new Date(this.state.currentMonth.getFullYear(), this.state.selectedMonth, 1);
            this.updateCalendar();
        },
        updateCalendar() {
            const year = this.state.currentMonth.getFullYear();
            const month = this.state.currentMonth.getMonth();
            
            // 해당 월의 첫 날과 마지막 날
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            
            // 첫 날의 요일 (0=일요일)
            const firstDayOfWeek = firstDay.getDay();
            
            // 이전 달의 마지막 날들
            const prevMonthLastDay = new Date(year, month, 0).getDate();
            
            const days = [];
            
            // 이전 달의 날짜들
            for (let i = firstDayOfWeek - 1; i >= 0; i--) {
                days.push({
                    date: prevMonthLastDay - i,
                    otherMonth: true,
                    key: `prev-${prevMonthLastDay - i}`
                });
            }
            
            // 현재 달의 날짜들
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            for (let date = 1; date <= lastDay.getDate(); date++) {
                const currentDate = new Date(year, month, date);
                currentDate.setHours(0, 0, 0, 0);
                
                const startDate = this.state.tempStartDate ? new Date(this.state.tempStartDate.getFullYear(), this.state.tempStartDate.getMonth(), this.state.tempStartDate.getDate()) : null;
                if (startDate) startDate.setHours(0, 0, 0, 0);
                
                const endDate = this.state.tempEndDate ? new Date(this.state.tempEndDate.getFullYear(), this.state.tempEndDate.getMonth(), this.state.tempEndDate.getDate()) : null;
                if (endDate) endDate.setHours(0, 0, 0, 0);
                
                const isStart = startDate && currentDate.getTime() === startDate.getTime();
                const isEnd = endDate && currentDate.getTime() === endDate.getTime();
                const inRange = startDate && endDate &&
                    currentDate.getTime() >= startDate.getTime() && currentDate.getTime() <= endDate.getTime();
                const isToday = currentDate.getTime() === today.getTime();
                
                days.push({
                    date: date,
                    otherMonth: false,
                    isStart,
                    isEnd,
                    inRange,
                    isToday,
                    key: `current-${date}`
                });
            }
            
            // 다음 달의 날짜들 (총 42개 셀을 채우기 위해)
            const remainingDays = 42 - days.length;
            for (let date = 1; date <= remainingDays; date++) {
                days.push({
                    date: date,
                    otherMonth: true,
                    key: `next-${date}`
                });
            }
            
            this.state.calendarDays = days;
        },
        getDateRangeText() {
            if (this.state.startDate && this.state.endDate) {
                return `${this.formatDisplayDate(new Date(this.state.startDate))} ~ ${this.formatDisplayDate(new Date(this.state.endDate))}`;
            }
            return '날짜 선택';
        },
        formatDisplayDate(date) {
            if (!date) return '';
            const d = date instanceof Date ? date : new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}.${month}.${day}`;
        },
        handleClickOutside(event) {
            const picker = this.$el.querySelector('.date-range-picker-wrapper');
            if (picker && !picker.contains(event.target) && this.state.showDatePicker) {
                this.closeDatePicker();
            }
        },
        setPeriod(months) {
            const endDate = new Date();
            const startDate = new Date();
            startDate.setMonth(startDate.getMonth() - months);
            
            this.state.startDate = this.formatDateForInput(startDate);
            this.state.endDate = this.formatDateForInput(endDate);
            this.fetchAnalytics();
        },
        formatDateForInput(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        fetchCrews() {
            this.state.loading.crews = true;
            return new Promise((resolve) => {
                this.axiosCaller.get(
                    this,
                    this.APIs.LICENSE + '/getUserCrewList',
                    {},
                    (result) => {
                        try {
                            const list = this.getSafeArray(result);
                            this.state.crews = list;
                        } finally {
                            this.state.loading.crews = false;
                            resolve();
                        }
                    }
                );
            });
        },
        fetchAnalytics() {
            if (!this.state.startDate || !this.state.endDate) {
                return;
            }
            
            this.state.loading.analytics = true;
            const params = {
                periodStart: this.state.startDate,
                periodEnd: this.state.endDate,
                crewId: this.state.selectedCrewId,
            };
            
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getLicenseAnalytics',
                params,
                (result) => {
                    try {
                        const data = this.getData(result);
                        if (data?.status === 'ok') {
                            this.state.analytics = data.analytics;
                        } else {
                            this.gritterErr(data?.msg || '데이터를 불러오는데 실패했습니다.');
                            this.state.analytics = null;
                        }
                    } catch (e) {
                        console.error('Analytics fetch error:', e);
                        this.state.analytics = null;
                    } finally {
                        this.state.loading.analytics = false;
                    }
                }
            );
        },
        // 공통 유틸 (원본에서 사용하던 형태 유지)
        getData(resOrData) { return resOrData?.data ?? resOrData; },
        getSafeArray(resOrData) {
            const result = this.getData(resOrData);
            if (result?.status && result.status !== 'ok') {
                this.gritterErr(result?.msg || '요청이 실패했습니다.');
                return [];
            }
            const topCandidates = [result?.list, result?.rows, result?.content, result?.data];
            for (const v of topCandidates) if (Array.isArray(v)) return v;
            const nestedCandidates = [result?.data?.list, result?.data?.rows, result?.data?.content];
            for (const v of nestedCandidates) if (Array.isArray(v)) return v;
            if (result && typeof result === 'object') {
                for (const k of Object.keys(result)) if (Array.isArray(result[k])) return result[k];
            }
            return [];
        },
        gritterErr(msg) { 
            const m = msg || '정상적인 접근이 아닙니다. 다시 시도해주세요.'; 
            this.swalUtils?.gritter ? this.swalUtils.gritter('error', m, 'error', 2000) : console.error('[ERR]', m); 
        },
    },
    computed: {
        availableYears() {
            const currentYear = new Date().getFullYear();
            const years = [];
            // 현재 년도 기준으로 앞뒤 10년씩
            for (let i = currentYear - 10; i <= currentYear + 10; i++) {
                years.push(i);
            }
            return years;
        },
    },
    mounted() {
        this.updateCalendar();
        this.fetchCrews().then(() => {
            this.fetchAnalytics();
        });
        
        // 외부 클릭 시 달력 닫기
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
};
</script>

<style scoped>
/* 컴포넌트 최상위 클래스로 모든 스타일을 감싸서 우선순위 높임 */
.lic-analytics {
    padding: 20px;
}

.lic-analytics .list-controls {
    margin-bottom: 30px !important;
}

.lic-analytics .gui-form .row-box {
    display: flex !important;
    gap: 20px !important;
    flex-wrap: wrap !important;
}

.lic-analytics .sel-inp-set {
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
}

.lic-analytics .sel-inp-set label {
    min-width: 60px !important;
    font-weight: 500 !important;
}

.lic-analytics .sel-inp-set select {
    min-width: 200px !important;
    padding: 8px !important;
    border: 1px solid #ddd !important;
    border-radius: 4px !important;
}

.lic-analytics .date-range-picker-wrapper {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
}

.lic-analytics .date-range-btn {
    padding: 8px 16px !important;
    border: 1px solid #ddd !important;
    border-radius: 4px !important;
    background-color: white !important;
    color: #333 !important;
    cursor: pointer !important;
    font-size: 13px !important;
    min-width: 200px !important;
    text-align: left !important;
    transition: background-color 0.2s, border-color 0.2s !important;
}

.lic-analytics .date-range-btn:hover {
    background-color: #f0f0f0 !important;
    border-color: #999 !important;
}

/* 날짜 선택 팝업 */
.lic-analytics .date-picker-popup {
    position: absolute !important;
    top: 100% !important;
    left: 0 !important;
    margin-top: 5px !important;
    background: white !important;
    border: 1px solid #ddd !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    z-index: 1000 !important;
    width: 280px !important;
    overflow: hidden !important;
}

/* 헤더 */
.lic-analytics .date-picker-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 12px 16px !important;
    border-bottom: 1px solid #eee !important;
}

.lic-analytics .date-picker-title {
    font-weight: 600 !important;
    font-size: 14px !important;
}

.lic-analytics .close-btn {
    background: none !important;
    border: none !important;
    font-size: 24px !important;
    line-height: 1 !important;
    cursor: pointer !important;
    color: #666 !important;
    padding: 0 !important;
    width: 24px !important;
    height: 24px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.lic-analytics .close-btn:hover {
    color: #333 !important;
}

/* 달력 영역 */
.lic-analytics .date-picker-calendar {
    padding: 12px !important;
    width: 100% !important;
    box-sizing: border-box !important;
}

/* 년도/월 선택 영역 - 달력 그리드와 정확히 같은 너비 */
.lic-analytics .calendar-month {
    display: flex !important;
    flex-direction: column !important;
    gap: 4px !important;
    margin-bottom: 6px !important;
    width: 100% !important;
    box-sizing: border-box !important;
}

/* 년도 선택 줄 */
.lic-analytics .year-selector-row {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr) !important;
    gap: 2px !important;
    align-items: center !important;
    width: 100% !important;
}

.lic-analytics .year-selector-row > .year-nav-btn:first-of-type {
    grid-column: 1 !important;
}

.lic-analytics .year-selector-row > .year-select {
    grid-column: 2 / 7 !important;
}

.lic-analytics .year-selector-row > .year-nav-btn:last-of-type {
    grid-column: 7 !important;
}

/* 월 선택 줄 */
.lic-analytics .month-selector-row {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr) !important;
    gap: 2px !important;
    align-items: center !important;
    width: 100% !important;
}

.lic-analytics .month-selector-row > .month-nav-btn:first-of-type {
    grid-column: 1 !important;
}

.lic-analytics .month-selector-row > .month-select {
    grid-column: 2 / 7 !important;
}

.lic-analytics .month-selector-row > .month-nav-btn:last-of-type {
    grid-column: 7 !important;
}

.lic-analytics .year-select,
.lic-analytics .month-select {
    width: 100% !important;
    padding: 6px 4px !important;
    border: 1px solid #ddd !important;
    border-radius: 4px !important;
    font-size: 12px !important;
    background-color: white !important;
    cursor: pointer !important;
    text-align: center !important;
    box-sizing: border-box !important;
}

.lic-analytics .year-select:hover,
.lic-analytics .month-select:hover {
    border-color: #999 !important;
}

.lic-analytics .year-select:focus,
.lic-analytics .month-select:focus {
    outline: none !important;
    border-color: #007bff !important;
}

.lic-analytics .year-nav-btn,
.lic-analytics .month-nav-btn {
    background: none !important;
    border: none !important;
    font-size: 18px !important;
    cursor: pointer !important;
    color: #666 !important;
    padding: 8px 4px !important;
    border-radius: 4px !important;
    transition: background-color 0.2s !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.lic-analytics .year-nav-btn:hover,
.lic-analytics .month-nav-btn:hover {
    background-color: #f0f0f0 !important;
}

/* 요일 헤더 */
.lic-analytics .calendar-weekdays {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr) !important;
    gap: 2px !important;
    margin-bottom: 6px !important;
    width: 100% !important;
    box-sizing: border-box !important;
}

.lic-analytics .weekday {
    text-align: center !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    color: #666 !important;
    padding: 8px 0 !important;
}

/* 날짜 그리드 */
.lic-analytics .calendar-days {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr) !important;
    gap: 2px !important;
    width: 100% !important;
    box-sizing: border-box !important;
}

.lic-analytics .calendar-day {
    aspect-ratio: 1 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    border-radius: 4px !important;
    font-size: 14px !important;
    transition: all 0.2s !important;
    position: relative !important;
}

.lic-analytics .calendar-day:hover:not(.other-month) {
    background-color: #f0f0f0 !important;
}

.lic-analytics .calendar-day.other-month {
    color: #ccc !important;
    cursor: default !important;
}

.lic-analytics .calendar-day.today {
    font-weight: 600 !important;
    color: #007bff !important;
}

.lic-analytics .calendar-day.in-range {
    background-color: #e3f2fd !important;
}

.lic-analytics .calendar-day.selected-start,
.lic-analytics .calendar-day.selected-end {
    background-color: #007bff !important;
    color: white !important;
    font-weight: 600 !important;
}

.lic-analytics .calendar-day.selected-start {
    border-top-left-radius: 4px !important;
    border-bottom-left-radius: 4px !important;
}

.lic-analytics .calendar-day.selected-end {
    border-top-right-radius: 4px !important;
    border-bottom-right-radius: 4px !important;
}

/* 푸터 */
.lic-analytics .date-picker-footer {
    padding: 12px 16px !important;
    border-top: 1px solid #eee !important;
}

.lic-analytics .selected-range {
    margin-bottom: 12px !important;
    font-size: 13px !important;
    color: #666 !important;
    text-align: center !important;
    min-height: 20px !important;
}

.lic-analytics .date-picker-actions {
    display: flex !important;
    gap: 8px !important;
    justify-content: flex-end !important;
}

.lic-analytics .cancel-btn,
.lic-analytics .apply-btn {
    padding: 8px 16px !important;
    border: 1px solid #ddd !important;
    border-radius: 4px !important;
    cursor: pointer !important;
    font-size: 13px !important;
    transition: all 0.2s !important;
}

.lic-analytics .cancel-btn {
    background-color: white !important;
    color: #333 !important;
}

.lic-analytics .cancel-btn:hover {
    background-color: #f0f0f0 !important;
}

.lic-analytics .apply-btn {
    background-color: #007bff !important;
    color: white !important;
    border-color: #007bff !important;
}

.lic-analytics .apply-btn:hover:not(:disabled) {
    background-color: #0056b3 !important;
    border-color: #0056b3 !important;
}

.lic-analytics .apply-btn:disabled {
    background-color: #ccc !important;
    border-color: #ccc !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
}

.lic-analytics .period-btn {
    padding: 8px 16px !important;
    border: 1px solid #ddd !important;
    border-radius: 4px !important;
    background-color: white !important;
    color: #333 !important;
    cursor: pointer !important;
    font-size: 13px !important;
    white-space: nowrap !important;
    min-width: fit-content !important;
    transition: background-color 0.2s, border-color 0.2s !important;
}

.lic-analytics .period-btn:hover {
    background-color: #f0f0f0 !important;
    border-color: #999 !important;
}

.lic-analytics .period-btn:active {
    background-color: #e0e0e0 !important;
}

.lic-analytics .analytics-content {
    margin-top: 30px !important;
}

.lic-analytics .stats-grid {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
    gap: 20px !important;
}

.lic-analytics .stat-card {
    background: #f8f9fa !important;
    border: 1px solid #e9ecef !important;
    border-radius: 8px !important;
    padding: 20px !important;
    text-align: center !important;
}

.lic-analytics .stat-label {
    font-size: 14px !important;
    color: #666 !important;
    margin-bottom: 10px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 5px !important;
}

.lic-analytics .tooltip-icon {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 16px !important;
    height: 16px !important;
    border-radius: 50% !important;
    background-color: #999 !important;
    color: white !important;
    font-size: 12px !important;
    font-weight: bold !important;
    cursor: help !important;
    position: relative !important;
}

.lic-analytics .tooltip-icon:hover::after {
    content: attr(title) !important;
    position: absolute !important;
    bottom: 100% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    margin-bottom: 5px !important;
    padding: 6px 10px !important;
    background-color: #333 !important;
    color: white !important;
    font-size: 12px !important;
    white-space: nowrap !important;
    border-radius: 4px !important;
    z-index: 1000 !important;
    pointer-events: none !important;
}

.lic-analytics .tooltip-icon:hover::before {
    content: '' !important;
    position: absolute !important;
    bottom: 100% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    margin-bottom: -1px !important;
    border: 5px solid transparent !important;
    border-top-color: #333 !important;
    z-index: 1000 !important;
    pointer-events: none !important;
}

.lic-analytics .stat-value {
    font-size: 28px !important;
    font-weight: bold !important;
    color: #333 !important;
}

.lic-analytics .stat-sub-label {
    font-size: 12px !important;
    color: #666 !important;
    margin-top: 12px !important;
    margin-bottom: 4px !important;
}

.lic-analytics .stat-sub-value {
    font-size: 20px !important;
    font-weight: bold !important;
    color: #333 !important;
}

.lic-analytics .muted {
    color: #666 !important;
    text-align: center !important;
    padding: 40px !important;
}
</style>
